import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './lib/api';
import type { Role } from './types';
import { CoordinatorQueue } from './screens/CoordinatorQueue';
import { SchedulerQueue } from './screens/SchedulerQueue';
import { RoleSwitcher } from './components/RoleSwitcher';
import styles from './App.module.css';

type View = 'coordinator' | 'scheduler';

function defaultView(role: Role): View {
    return role === 'SCHEDULER' || role === 'DAVE' || role === 'PE' ? 'scheduler' : 'coordinator';
}

export default function App() {
    const me = useQuery({ queryKey: ['me'], queryFn: api.me });
    const [view, setView] = useState<View | null>(null);

    let body: ReactNode;
    let tabs: ReactNode = null;
    let who: ReactNode = null;

    if (me.isLoading) {
        body = <p className={styles.state}>Authenticating with monday…</p>;
    } else if (me.isError || !me.data) {
        body = (
            <p className={styles.error}>Could not authenticate. Reload the app inside monday.</p>
        );
    } else {
        const { role, name, id } = me.data;
        const active = view ?? defaultView(role);
        who = (
            <span className={styles.who}>
                {name} · {role}
            </span>
        );
        if (role === 'ADMIN') {
            tabs = (
                <span className={styles.tabs}>
                    <button
                        className={active === 'coordinator' ? 'primary' : undefined}
                        onClick={() => setView('coordinator')}
                    >
                        Coordinator
                    </button>
                    <button
                        className={active === 'scheduler' ? 'primary' : undefined}
                        onClick={() => setView('scheduler')}
                    >
                        Scheduler
                    </button>
                </span>
            );
        }
        body = active === 'coordinator' ? <CoordinatorQueue meId={id} /> : <SchedulerQueue />;
    }

    return (
        <div>
            {import.meta.env.DEV && <RoleSwitcher />}
            <header className={styles.header}>
                <strong>Delivery Improvement System</strong>
                {who}
                {tabs}
            </header>
            {body}
        </div>
    );
}
